import {
  Box,
  Button,
  Container,
  Grid,
  Hidden,
  Typography,
  useTheme
} from "@mui/material";
import moment from "moment";
import React, {
  useContext,
  // useContext,
  useEffect,
  useState
} from "react";
import { useNavigate, useParams } from "react-router";
import {
  BCPArrowLeft,
  BCPCalendarDays,
  BCPChartMixed,
  BCPCircleInfo,
  BCPEye,
  BCPEyeSlash,
  BCPPen,
  BCPShop,
  BCPUserMagnifyingGlass,
  BCPUsersViewfinder
} from "../../../components/icons/bcp/BCPIcons";
import VerifiedIcon from "../../../components/icons/VerifiedIcon";
import Hero from "../../../components/layout/Hero";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import { AuthContext } from "../../../components/user/BCPAuthProvider";
import { postNotification } from "../../../components/utilities/Notifications";
import {
  // comingSoon,
  findGetParameter,
  getData,
  postData
} from "../../../components/utilities/UtilityFunctions";
import ExternalLink from "../../../components/utilities/ExternalLink";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import OrgDashEvents from "./OrganizationDashboard/OrgDashEvents";
import OrgDashOverview from "./OrganizationDashboard/OrgDashOverview";
import OrgDashRankings from "./OrganizationDashboard/OrgDashRankings";
import { hasAccess } from "../OrganizationOrganize";
import OrganizationHelmet from "./subcomponents/OrganizationHelmet";
import { testOrgs } from "../utils/testData";
import { useTranslation } from "react-i18next";
import { isAppMode } from "../../../global/AppOverlay";

const SYSTEM_IMAGES_CLOUDFRONT = getEnvironmentVariable(
  "SYSTEM_IMAGES_CLOUDFRONT"
);

export default function OrganizationDashboard(props) {
  const { organize, handleTabChange } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "pages.dashboards.organization"
  });

  const { organizationId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const appMode = isAppMode();

  const [loading, setLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(true);
  const [organization, setOrganization] = useState(props?.organization || null);

  const [upcomingEvents, setUpcomingEvents] = useState(null);
  const [allEvents, setAllEvents] = useState(null);

  const [leaderboardPreview, setLeaderboardPreview] = useState(null);
  const [allLeaderboards, setAllLeaderboards] = useState(null);

  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const [tab, setTab] = useState(
    findGetParameter("active_tab") === "events"
      ? "events"
      : findGetParameter("active_tab") === "leaderboards"
        ? "leaderboards"
        : "overview"
  );

  // Check if we're in an embedded environment
  const embed = findGetParameter("embed") === "true";

  const [renderEditButton, setRenderEditButton] = useState(false);
  const [renderOrganizeButton, setRenderOrganizeButton] = useState(false);
  const [gameSystems, setGameSystems] = useState(null);

  const fetchUpcomingEvents = async () => {
    let queryStringParameters = {
      limit: 31,
      gameStoreId: organizationId,
      startDate: `${moment()
        .subtract(3, "days")
        .format("YYYY-MM-DD")}T00:00:00Z`,
      "expand[]": ["gameSystem"]
    };
    if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
      queryStringParameters.gameSystemId =
        getEnvironmentVariable("SINGLE_GAME_SYSTEM");
    } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
      queryStringParameters.gameSystemManufacturer = getEnvironmentVariable(
        "SINGLE_GAME_MANUFACTURER"
      );
    }

    let apiEvents = await getData("/events", {
      queryStringParameters: queryStringParameters
    });
    apiEvents = apiEvents.data;
    apiEvents.forEach((event) => {
      if (event.pricingDict) {
        const keys = Object.keys(event.pricingDict);
        if (keys.length > 0) {
          event.pricingInfo = {
            currency: keys[0],
            amount: (event.pricingDict[keys[0]] / 100).toFixed(2)
          };
        }
      }
    });

    return apiEvents;
  };

  const fetchLeaderboardPreview = async () => {
    const apiLeagues = await getData("/leagues", {
      limit: 3,
      gameStoreId: organizationId,
      "expand[]": ["gameSystem"]
    });

    if (apiLeagues?.data) {
      const leaderboards = [];
      for (let i = 0; i < apiLeagues.data.length; i++) {
        const league = apiLeagues.data[i];
        const apiLeaderboard = await getData("/placings", {
          placingsType: "player",
          limit: 5,
          leagueId: league.id,
          sortAscending: false
        });
        leaderboards.push({
          league: league,
          leaderboards: apiLeaderboard.data
        });
      }

      return leaderboards;
    }
  };

  const parallel = async () => {
    return {
      taskUpcomingEvents: await fetchUpcomingEvents(),
      taskLeaderboardPreview: await fetchLeaderboardPreview()
    };
  };

  const overviewSetup = async () => {
    try {
      if (upcomingEvents === null && leaderboardPreview === null) {
        try {
          const { taskUpcomingEvents, taskLeaderboardPreview } =
            await parallel();

          setUpcomingEvents(taskUpcomingEvents);
          setLeaderboardPreview(taskLeaderboardPreview);
        } catch (err) {
          postNotification("error", err);
          setUpcomingEvents([]);
          setLeaderboardPreview([]);
        }
      } else {
        if (upcomingEvents === null) {
          try {
            const apiEvents = await fetchUpcomingEvents();

            setUpcomingEvents(apiEvents);
          } catch (err) {
            postNotification("error", err);
            setUpcomingEvents([]);
          }
        }

        if (leaderboardPreview === null) {
          try {
            const apiLeaderboards = await fetchLeaderboardPreview();

            setLeaderboardPreview(apiLeaderboards);
          } catch (err) {
            postNotification("error", err);
            setLeaderboardPreview([]);
          }
        }
      }
    } catch (err) {
      postNotification("error", err);
    } finally {
      setSubLoading(false);
    }
  };

  const getButtonStyles = (t) => {
    return {
      color:
        t === tab
          ? theme.palette.secondary.contrastText
          : theme.palette.text.primary,
      backgroundColor:
        t === tab
          ? theme.palette.secondary.main
          : theme.palette.background.main,
      textTransform: "none",
      fontWeight: "600",
      fontSize: "18px",
      "&:hover": {
        backgroundColor:
          t === tab
            ? `${theme.palette.text.disabled} !important`
            : theme.palette.secondary.main,
        color:
          t === tab
            ? `${theme.palette.secondary.contrastText} !important`
            : theme.palette.secondary.contrastText
      }
    };
  };

  const handleFollow = async () => {
    if (!authContext?.authState?.identity?.userId) {
      postNotification("error", t("notifications.errors.pleaseSignInToFollow"));
      return;
    }

    try {
      setFollowLoading(true);

      const endpoint = isFollowing
        ? `/gamestores/${organizationId}/unfollow`
        : `/gamestores/${organizationId}/follow`;

      await postData(endpoint);

      setIsFollowing(!isFollowing);

      const updatedOrgBase = await getData(`/gamestores/${organizationId}`, {
        queryStringParameters: {
          "expand[]": ["location"]
        }
      });

      let newOrgData = { ...updatedOrgBase };

      if (newOrgData?.photoFileId) {
        const photoData = await getData(`/files/${newOrgData.photoFileId}`, {
          queryStringParameters: {}
        });
        newOrgData.photo = photoData;
        newOrgData.photoUrl = photoData?.url;
      } else {
        newOrgData.photo = null;
        newOrgData.photoUrl = null;
      }

      if (newOrgData?.profileFileId) {
        const profileData = await getData(
          `/files/${newOrgData.profileFileId}`,
          {
            queryStringParameters: {}
          }
        );
        newOrgData.profile = profileData;
        newOrgData.profileUrl = profileData?.url;
      } else {
        newOrgData.profile = null;
        newOrgData.profileUrl = null;
      }
      if (testOrgs?.[organizationId]) {
        newOrgData = { ...newOrgData, ...testOrgs[organizationId] };
      }

      setOrganization(newOrgData);
    } catch (err) {
      postNotification("error", err);
    } finally {
      setFollowLoading(false);
    }
  };

  useEffect(() => {
    const check = async () => {
      setLoading(true);
      try {
        if (organizationId) {
          let apiStore = organization
            ? organization
            : await getData(`/gamestores/${organizationId}`, {
                queryStringParameters: {
                  "expand[]": ["location"]
                }
              });

          if (apiStore?.photoFileId) {
            apiStore.photo = await getData(`/files/${apiStore.photoFileId}`, {
              queryStringParameters: {}
            });
          }

          if (apiStore?.profileFileId) {
            apiStore.profile = await getData(
              `/files/${apiStore.profileFileId}`,
              {
                queryStringParameters: {}
              }
            );
          }
          if (apiStore?.websiteURL) {
            if (!/^https?:\/\//i.test(apiStore.websiteURL)) {
              apiStore.websiteURL = "http://" + apiStore.websiteURL;
            }
          }

          if (testOrgs?.[organizationId]) {
            apiStore = { ...apiStore, ...testOrgs[organizationId] };
          }

          // Check if user has access to edit the page
          try {
            const access = await hasAccess(apiStore, authContext, 1, false);

            if (access) {
              setRenderOrganizeButton(true);
              setRenderEditButton(true);
            }
          } catch {
            try {
              const access = await hasAccess(apiStore, authContext, 0, false);

              if (access) {
                setRenderOrganizeButton(true);
              }
            } catch {}
          }

          if (authContext?.authState?.identity?.userId) {
            try {
              const userData = await getData(
                `/users/${authContext.authState.identity.userId}`
              );
              const isUserFollowing =
                userData?.followingOrganizationIds?.includes(organizationId) ||
                false;
              setIsFollowing(isUserFollowing);
            } catch (err) {
              console.error("Error fetching following status:", err);
              setIsFollowing(false);
            }
          }
          setOrganization(apiStore);
        }
      } catch (err) {
        postNotification("error", err);
      } finally {
        setLoading(false);
      }
    };
    check();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (organization) {
      overviewSetup();
    }
    // eslint-disable-next-line
  }, [organization, organizationId]);

  return (
    <Box width="100%">
      {!organize && organization && (
        <OrganizationHelmet organization={organization} />
      )}

      <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
        {Boolean(loading) && <ModalLoadingFull />}

        <Grid container spacing={2} sx={{ p: 2 }}>
          {!appMode && (
            <>
              {organize && (
                <Grid item xs={12} md={6}>
                  <ExternalLink
                    href={`/organize/organization/${organizationId}${embed ? "?embed=true" : ""}`}
                    target="_blank"
                    color="link.main"
                    sx={{
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start"
                    }}
                  >
                    <BCPArrowLeft
                      sx={{ "padding-right": "1rem", "padding-bottom": "2px" }}
                    />{" "}
                    {t("backToMyOrganizations")}
                  </ExternalLink>
                </Grid>
              )}

              {!organize ? (
                <>
                  {renderOrganizeButton && (
                    <Grid
                      item
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <ExternalLink
                        href={`/organize/organization/${organizationId}${embed ? "?embed=true" : ""}`}
                        color="link.main"
                        sx={{
                          textDecoration: "none",
                          display: "flex",
                          gap: "1rem"
                        }}
                      >
                        {t("viewAsOrganizer")}
                        <BCPUserMagnifyingGlass
                          sx={{ "padding-bottom": "2px" }}
                        />
                      </ExternalLink>
                    </Grid>
                  )}
                </>
              ) : (
                <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                  <ExternalLink
                    href={`/organization/${organizationId}${embed ? "?embed=true" : ""}`}
                    color="link.main"
                    sx={{
                      textDecoration: "none",
                      display: "flex",
                      justifyContent: "flex-end",
                      flexDirection: { xs: "row-reverse", md: "row" },
                      gap: "1rem"
                    }}
                  >
                    {t("publicView")}
                    <BCPUsersViewfinder sx={{ "padding-bottom": "2px" }} />
                  </ExternalLink>
                </Grid>
              )}
            </>
          )}

          <Grid item xs={12}>
            <Hero
              backgroundImage={
                !loading || organization
                  ? organization?.photoUrl ||
                    `${SYSTEM_IMAGES_CLOUDFRONT}/images/EventImage${
                      theme.palette.mode === "dark" ? "Dark" : ""
                    }.png`
                  : ""
              }
              sxBackground={{
                height: ["unset", "20vh"],
                maxHeight: ["unset", "600px"],
                minHeight: "unset",
                color: "common.white",
                borderRadius: "4px"
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid
                item
                xs={4}
                md={2}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Box
                  sx={{
                    minHeight: ["100px", "150px"],
                    minWidth: ["100px", "150px"],
                    maxHeight: "200px",
                    maxWidth: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    borderRadius: "50%",
                    backgroundColor: theme.palette.background.main,
                    border: `1px solid ${theme.palette.divider}`,
                    ...(organization?.profileUrl && {
                      backgroundImage: `url(${organization?.profileUrl})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center"
                    })
                  }}
                >
                  {!organization?.profileUrl && <BCPShop size={"3rem"} />}
                </Box>
              </Grid>
              <Grid
                item
                xs={8}
                md={10}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: ["center", "flex-start"],
                  flexDirection: "column"
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 700, fontSize: "36px" }}
                  >
                    {organization?.name || "---"}{" "}
                    {organization?.isVerified && <VerifiedIcon />}
                  </Typography>
                </Box>

                {getOrgType(organization) && (
                  <Typography variant="body1">
                    {`${t(getOrgType(organization))} ${
                      getOrgLocation(organization)
                        ? `${t("in")} ${getOrgLocation(organization)}`
                        : ``
                    }`}
                  </Typography>
                )}

                <Hidden smDown>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700 }}
                    gutterBottom
                  >
                    {organization?.numberOfFollowers >= 0
                      ? String(organization?.numberOfFollowers)
                      : "0"}{" "}
                    {organization?.numberOfFollowers !== 1
                      ? t("followers")
                      : t("follower")}{" "}
                    |{" "}
                    {upcomingEvents?.length <= 30
                      ? `${upcomingEvents.length}`
                      : upcomingEvents?.length > 30
                        ? "30+"
                        : "0"}{" "}
                    {upcomingEvents?.length !== 1
                      ? t("upcomingEvents")
                      : t("upComingEvent")}
                  </Typography>
                </Hidden>
                <Hidden smUp>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {organization?.numberOfFollowers >= 0
                      ? String(organization?.numberOfFollowers)
                      : "0"}{" "}
                    {organization?.numberOfFollowers !== 1
                      ? t("followers")
                      : t("follower")}{" "}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700 }}
                    gutterBottom
                  >
                    {upcomingEvents?.length <= 30
                      ? `${upcomingEvents.length}`
                      : upcomingEvents?.length > 30
                        ? "30+"
                        : "0"}{" "}
                    {upcomingEvents?.length !== 1
                      ? t("upcomingEvents")
                      : t("upComingEvent")}
                  </Typography>
                </Hidden>

                {organization?.description && (
                  <Hidden smDown>
                    <Typography variant="body1">
                      {organization?.description}
                    </Typography>
                  </Hidden>
                )}
              </Grid>
            </Grid>
          </Grid>

          {organization?.description && (
            <Hidden smUp>
              <Grid item xs={12}>
                <Typography variant="body1">
                  {organization?.description}
                </Typography>
              </Grid>
            </Hidden>
          )}

          {/* Tabs */}
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              mt: 2
            }}
          >
            <Box sx={{ display: "flex", gap: [1, 2], flexWrap: "wrap" }}>
              <Button
                sx={getButtonStyles("overview")}
                startIcon={<BCPCircleInfo size={20} />}
                onClick={(e) => {
                  setTab("overview");
                  navigate(
                    `${organize ? "/organize" : ""}/organization/${
                      organizationId || organization?.id
                    }?active_tab=overview${embed ? "&embed=true" : ""}`,
                    { replace: false }
                  );
                }}
              >
                {t("overview")}
              </Button>
              <Button
                sx={getButtonStyles("events")}
                startIcon={<BCPCalendarDays size={20} />}
                onClick={(e) => {
                  setTab("events");
                  navigate(
                    `${organize ? "/organize" : ""}/organization/${
                      organizationId || organization?.id
                    }?active_tab=events${embed ? "&embed=true" : ""}`,
                    { replace: false }
                  );
                }}
              >
                {t("events")}
              </Button>
              <Button
                sx={getButtonStyles("leaderboards")}
                startIcon={<BCPChartMixed size={20} />}
                onClick={(e) => {
                  setTab("leaderboards");
                  navigate(
                    `${organize ? "/organize" : ""}/organization/${
                      organizationId || organization?.id
                    }?active_tab=leaderboards${embed ? "&embed=true" : ""}`,
                    { replace: false }
                  );
                }}
              >
                {t("leaderboards")}
              </Button>

              <Hidden smUp>
                {organize && renderEditButton ? (
                  <Button
                    variant="contained"
                    startIcon={<BCPPen size={20} />}
                    onClick={(e) => {
                      if (handleTabChange) {
                        handleTabChange(e, 1);
                      }
                    }}
                  >
                    {t("editPage")}
                  </Button>
                ) : (
                  <Button
                    variant={isFollowing ? "outlined" : "contained"}
                    startIcon={
                      isFollowing ? (
                        <BCPEyeSlash size={20} />
                      ) : (
                        <BCPEye size={20} />
                      )
                    }
                    onClick={handleFollow}
                    disabled={followLoading}
                  >
                    {isFollowing ? t("unfollow") : t("follow")}
                  </Button>
                )}
              </Hidden>
            </Box>

            <Hidden smDown>
              {organize && renderEditButton ? (
                <Button
                  variant="contained"
                  startIcon={<BCPPen size={20} />}
                  onClick={(e) => {
                    if (handleTabChange) {
                      handleTabChange(e, 1);
                    } else {
                      navigate(
                        `/organize/organization/${
                          organizationId || organization?.id
                        }?active_tab=details${embed ? "&embed=true" : ""}`,
                        { replace: true }
                      );
                    }
                  }}
                >
                  {t("editPage")}
                </Button>
              ) : (
                <Button
                  variant={isFollowing ? "outlined" : "contained"}
                  startIcon={
                    isFollowing ? (
                      <BCPEyeSlash size={20} />
                    ) : (
                      <BCPEye size={20} />
                    )
                  }
                  onClick={handleFollow}
                  disabled={followLoading}
                >
                  {isFollowing ? t("unfollow") : t("follow")}
                </Button>
              )}
            </Hidden>
          </Grid>

          {tab === "overview" && (
            <Grid item xs={12}>
              <OrgDashOverview
                upcomingEvents={upcomingEvents}
                setUpcomingEvents={setUpcomingEvents}
                leaderboardPreview={leaderboardPreview}
                setLeaderboardPreview={setLeaderboardPreview}
                organization={organization}
                tab={tab}
                setTab={setTab}
                organize={organize}
                authContext={authContext}
                loading={subLoading}
                t={t}
              />
            </Grid>
          )}

          {tab === "events" && (
            <Grid item xs={12}>
              {organization && (
                <OrgDashEvents
                  allEvents={allEvents}
                  setAllEvents={setAllEvents}
                  organization={organization}
                  organize={organize}
                  gameSystems={gameSystems}
                  setGameSystems={setGameSystems}
                />
              )}
            </Grid>
          )}

          {tab === "leaderboards" && (
            <Grid item xs={12}>
              <OrgDashRankings
                allLeaderboards={allLeaderboards}
                setAllLeaderboards={setAllLeaderboards}
                organization={organization}
                organize={organize}
                gameSystems={gameSystems}
                setGameSystems={setGameSystems}
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}

function getOrgType(org) {
  if (org) {
    const orgTypes = org.organizationTypes;

    if (orgTypes?.club) {
      return "gamingClub";
    } else if (orgTypes?.store) {
      return "gameStore";
    } else if (orgTypes?.publisher) {
      return "publisher";
    } else if (orgTypes?.tournamentOrganizer) {
      return "independentTournamentOrganizer";
    } else {
      return null;
    }
  } else {
    return null;
  }
}

function getOrgLocation(org) {
  if (!org?.location) return null;
  if (typeof org.location === "object") {
    if (!Object.keys(org.location).length) return null;
  }

  const city = org?.location.city;
  const state = org?.location.state;
  const country = org?.location.country;

  const location = `${city ? `${city}, ` : ``}${state ? `${state} ` : ``}${
    country !== "United States" ? `${city || state ? ", " : ""}${country}` : ``
  }`.trim();

  return location || null;
}
